import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Brain, Filter, Eye, Trash2, X, Loader2, Calendar as CalendarIcon } from "lucide-react";
import { format, parseISO } from "date-fns";
import { frCA } from "date-fns/locale";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

function DateFilter({ value, onChange, placeholder, testid }) {
  const selected = value ? parseISO(value) : undefined;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={testid}
                className="flex items-center gap-2 border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 min-w-[9rem]">
          <CalendarIcon className="h-4 w-4 text-slate-400" />
          {selected ? format(selected, "d MMM yyyy", { locale: frCA }) : <span className="text-slate-400">{placeholder}</span>}
          {value && (
            <X className="h-3.5 w-3.5 ml-auto text-slate-400 hover:text-red-500"
               onClick={(e) => { e.stopPropagation(); onChange(""); }} />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={selected} locale={frCA}
                  onSelect={(d) => onChange(d ? format(d, "yyyy-MM-dd") : "")} />
      </PopoverContent>
    </Popover>
  );
}

const STATUT_BADGE = {
  en_attente: "text-slate-600 bg-slate-100",
  en_cours: "text-violet-700 bg-violet-100",
  termine: "text-teal-700 bg-teal-100",
  erreur: "text-red-700 bg-red-100",
  annule: "text-amber-700 bg-amber-100",
};
const STATUT_KEYS = ["en_attente", "en_cours", "termine", "erreur", "annule"];
const LAUNCH_ROLES = ["chef_service", "pathologiste", "pathologiste_distant", "technicien"];

export default function Analyses() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ statut: "", modele_id: "", date_debut: "", date_fin: "" });
  const canLaunch = LAUNCH_ROLES.includes(user?.role);
  const canDelete = user?.role === "chef_service";
  const stLabel = (k) => t(`ana_st_${k}`);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await api.get("/analyses", { params });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/modeles-ia").then(({ data }) => setModels(data)).catch(() => {}); }, []);

  const cancel = async (id, e) => {
    e.stopPropagation();
    try { await api.post(`/analyses/${id}/cancel`); toast.success(t("an_cancelled")); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm(t("an_delete_confirm"))) return;
    try { await api.delete(`/analyses/${id}`); setItems((p) => p.filter((a) => a.id !== id)); toast.success(t("an_deleted")); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="analyses-page">
      <div>
        <h1 className="font-head text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Brain className="h-6 w-6 text-violet-600" /> {t("an_title")}
        </h1>
        <p className="text-sm text-slate-500 mt-1">{t("an_subtitle")}</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-end gap-3" data-testid="analyses-filters">
        <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500"><Filter className="h-4 w-4" /> {t("an_filters")}</div>
        <select value={filters.statut} onChange={(e) => setFilters({ ...filters, statut: e.target.value })}
                data-testid="analyses-filter-statut" className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm">
          <option value="">{t("an_all_statuts")}</option>
          {STATUT_KEYS.map((k) => <option key={k} value={k}>{stLabel(k)}</option>)}
        </select>
        <select value={filters.modele_id} onChange={(e) => setFilters({ ...filters, modele_id: e.target.value })}
                data-testid="analyses-filter-modele" className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm">
          <option value="">{t("an_all_models")}</option>
          {models.map((m) => <option key={m.id} value={m.id}>{m.nom} v{m.version}</option>)}
        </select>
        <DateFilter value={filters.date_debut} onChange={(v) => setFilters({ ...filters, date_debut: v })}
                    placeholder={t("an_date_start")} testid="analyses-filter-debut" />
        <DateFilter value={filters.date_fin} onChange={(v) => setFilters({ ...filters, date_fin: v })}
                    placeholder={t("an_date_end")} testid="analyses-filter-fin" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden hidden md:block">
        <table className="w-full text-sm clin-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2.5">{t("an_col_date")}</th>
              <th className="text-left px-4 py-2.5">{t("an_col_model")}</th>
              <th className="text-left px-4 py-2.5">{t("an_col_statut")}</th>
              <th className="text-left px-4 py-2.5">{t("an_col_duree")}</th>
              <th className="text-left px-4 py-2.5">{t("an_col_auteur")}</th>
              <th className="text-right px-4 py-2.5">{t("an_col_actions")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" className="text-center py-12 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan="6" className="text-center py-12 text-slate-400" data-testid="analyses-empty">{t("an_empty")}</td></tr>
            ) : items.map((a) => (
              <tr key={a.id} onClick={() => navigate(`/analyses/${a.id}`)} data-testid={`analyse-row-${a.id}`}
                  className="border-t border-slate-100 cursor-pointer">
                <td className="px-4 py-2.5 text-slate-600">{new Date(a.created_at).toLocaleString("fr-CA")}</td>
                <td className="px-4 py-2.5 font-medium text-slate-800">{a.modele_nom} <span className="text-xs text-slate-400">v{a.version_modele}</span></td>
                <td className="px-4 py-2.5"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUT_BADGE[a.statut]}`}>{stLabel(a.statut)}</span></td>
                <td className="px-4 py-2.5 text-slate-500">{a.duree_secondes != null ? `${a.duree_secondes}s` : "—"}</td>
                <td className="px-4 py-2.5 text-slate-500 truncate max-w-[10rem]">{a.lance_par_nom || "—"}</td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/analyses/${a.id}`); }}
                          className="text-slate-400 hover:text-sky-600 p-1" data-testid={`analyse-view-${a.id}`}><Eye className="h-4 w-4" /></button>
                  {canLaunch && a.statut === "en_attente" && (
                    <button onClick={(e) => cancel(a.id, e)} className="text-slate-400 hover:text-amber-600 p-1" data-testid={`analyse-cancel-${a.id}`}><X className="h-4 w-4" /></button>
                  )}
                  {canDelete && (
                    <button onClick={(e) => remove(a.id, e)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`analyse-delete-${a.id}`}><Trash2 className="h-4 w-4" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Vue cartes — mobile */}
      <div className="md:hidden space-y-3" data-testid="analyses-cards">
        {loading ? (
          <div className="text-center py-12 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-slate-400" data-testid="analyses-empty-m">{t("an_empty")}</div>
        ) : items.map((a) => (
          <div key={a.id} onClick={() => navigate(`/analyses/${a.id}`)} data-testid={`analyse-card-${a.id}`}
               className="bg-white border border-slate-200 rounded-xl p-4 cursor-pointer active:bg-slate-50 transition-colors">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-slate-800 text-sm">{a.modele_nom} <span className="text-xs text-slate-400">v{a.version_modele}</span></span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUT_BADGE[a.statut]}`}>{stLabel(a.statut)}</span>
            </div>
            <div className="mt-1 text-xs text-slate-500">{new Date(a.created_at).toLocaleString("fr-CA")}</div>
            <div className="mt-1 text-xs text-slate-500">{t("an_duree")} : {a.duree_secondes != null ? `${a.duree_secondes}s` : "—"} · {a.lance_par_nom || "—"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
